import { AppEvents } from '@grafana/data';
import { t } from '@grafana/i18n';
import { appEvents } from 'app/core/app_events';
import { getConfig } from 'app/core/config';

import { SAVED_QUERIES_BASE_URL } from '../routes';

export function buildSavedQueryShareUrl(uid: string): string {
  return `${getConfig().appUrl.replace(/\/$/, '')}${SAVED_QUERIES_BASE_URL}/${uid}`;
}

/**
 * One-click share: copies the query's detail page link and confirms with a toast (or
 * reports failure with one). Returns whether the copy succeeded so callers can gate
 * follow-ups like analytics on it. The share drawer stays available for anything
 * beyond the link (e.g. query text).
 */
export async function copySavedQueryShareUrl(uid: string): Promise<boolean> {
  const url = buildSavedQueryShareUrl(uid);
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
    } else {
      // The Clipboard API requires a secure context; plain-HTTP deployments fall
      // back to the legacy selection-based copy.
      legacyCopy(url);
    }
  } catch {
    appEvents.emit(AppEvents.alertError, [t('saved-queries.share.link-copy-failed', 'Could not copy the link')]);
    return false;
  }
  appEvents.emit(AppEvents.alertSuccess, [t('saved-queries.share.link-copied', 'Link copied')]);
  return true;
}

function legacyCopy(text: string) {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  // Keep the textarea next to the triggering control so it remains inside modal
  // focus traps without nesting an interactive element inside a button.
  const container =
    document.activeElement instanceof HTMLElement ? (document.activeElement.parentElement ?? document.body) : document.body;
  container.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    if (!document.execCommand('copy')) {
      throw new Error('execCommand copy failed');
    }
  } finally {
    textarea.remove();
  }
}
